import React, { useState, useEffect, useRef } from "react";
import { Briefcase, Users, Scale, Building2 } from "lucide-react";

// CountUp component
const CountUp = ({ end, duration = 2000, isPercentage = false }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrameId;

    const startAnimation = (timestamp) => {
      startTime = timestamp;
      animate(timestamp);
    };

    const animate = (timestamp) => {
      const runtime = timestamp - startTime;
      const progress = Math.min(runtime / duration, 1);
      setCount(Math.floor(progress * end));

      if (runtime < duration) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrameId = requestAnimationFrame(startAnimation);
    return () => cancelAnimationFrame(animationFrameId);
  }, [end, duration, isPercentage]);

  return (
    <span>
      {count}
      {isPercentage && "%"}
    </span>
  );
};

// Main statistics component
const StatisticsCounter = ({ stats }) => {
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  const iconMap = {
    briefcase: Briefcase,
    users: Users,
    scale: Scale,
    building: Building2,
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={counterRef} className="container text-white py-4 mt-5">
      <div className="w-full mx-auto">
        <div className="w-full grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const IconComponent = iconMap[stat.icon];
            return (
              <div
                key={index}
                className="w-full flex items-center justify-center ml-2"
              >
                {IconComponent && (
                  <IconComponent className="w-12 h-12 text-red-600 mr-4" />
                )}
                <div>
                  <div className="text-4xl font-bold text-gray-800">
                    {isVisible ? (
                      <CountUp
                        end={stat.number}
                        isPercentage={stat.isPercentage || false}
                      />
                    ) : stat.isPercentage ? (
                      "0%"
                    ) : (
                      "0"
                    )}
                  </div>
                  <div className="text-sm text-gray-700 mt-1">{stat.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatisticsCounter;
